import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles, categories } from '@/mocks/articles';

const articleHeroImages: Record<string, string> = {
  'article-entry-guide': 'https://readdy.ai/api/search-image?query=A%20modern%20clean%20digital%20illustration%20of%20a%20secure%20web%20browser%20window%20showing%20a%20platform%20portal%20entrance%20with%20a%20glowing%20entry%20gate%20symbol%2C%20warm%20orange%20and%20teal%20accent%20colors%2C%20minimalist%20flat%20design%20style%2C%20soft%20gradient%20background%20in%20light%20cream%20tones%2C%20abstract%20geometric%20shapes%20representing%20connectivity%20and%20security%2C%20professional%20and%20trustworthy%20atmosphere&width=1600&height=700&seq=hero-entry-guide&orientation=landscape',
  'article-registration': 'https://readdy.ai/api/search-image?query=A%20clean%20and%20modern%20digital%20illustration%20of%20a%20user%20registration%20interface%20on%20a%20laptop%20screen%2C%20showing%20a%20friendly%20signup%20form%20with%20soft%20warm%20lighting%2C%20minimalist%20design%20with%20orange%20and%20cream%20tones%2C%20welcoming%20atmosphere&width=1600&height=700&seq=hero-registration&orientation=landscape',
  'article-features': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showcasing%20multiple%20service%20platform%20features%20as%20interconnected%20floating%20cards%20with%20icons%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimalist%20flat%20design%2C%20light%20cream%20background&width=1600&height=700&seq=hero-features&orientation=landscape',
  'article-security': 'https://readdy.ai/api/search-image?query=A%20modern%20cybersecurity%20themed%20digital%20illustration%20showing%20a%20shield%20icon%20protecting%20digital%20assets%2C%20warm%20dark%20background%20with%20orange%20and%20teal%20accent%20lighting%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-security&orientation=landscape',
  'article-update': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showing%20a%20platform%20update%20announcement%20with%20glowing%20notification%20cards%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-update&orientation=landscape',
  'article-faq-account': 'https://readdy.ai/api/search-image?query=A%20modern%20customer%20support%20illustration%20with%20a%20friendly%20help%20desk%20scene%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design%2C%20question%20mark%20icons&width=1600&height=700&seq=hero-faq-account&orientation=landscape',
  'article-comparison': 'https://readdy.ai/api/search-image?query=A%20modern%20comparison%20chart%20illustration%20showing%20multiple%20platform%20feature%20cards%20side%20by%20side%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-comparison&orientation=landscape',
  'article-mobile': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20of%20a%20smartphone%20displaying%20a%20clean%20mobile%20app%20interface%20with%20warm%20orange%20accents%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-mobile&orientation=landscape',
  'article-community': 'https://readdy.ai/api/search-image?query=A%20modern%20illustration%20representing%20online%20community%20guidelines%20and%20respectful%20communication%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-community&orientation=landscape',
  'article-access-faq': 'https://readdy.ai/api/search-image?query=A%20modern%20technical%20troubleshooting%20illustration%20showing%20network%20diagnostics%20with%20glowing%20connection%20nodes%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design&width=1600&height=700&seq=hero-access-faq&orientation=landscape',
};

const relatedThumbImages: Record<string, string> = {
  'article-entry-guide': 'https://readdy.ai/api/search-image?query=A%20modern%20clean%20digital%20illustration%20of%20a%20secure%20web%20browser%20window%20showing%20a%20platform%20portal%20entrance%2C%20warm%20orange%20and%20teal%20accent%20colors%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-entry-guide&orientation=landscape',
  'article-registration': 'https://readdy.ai/api/search-image?query=A%20clean%20and%20modern%20digital%20illustration%20of%20a%20user%20registration%20interface%20on%20a%20laptop%20screen%2C%20soft%20warm%20lighting%2C%20minimalist%20design&width=600&height=400&seq=rel-registration&orientation=landscape',
  'article-features': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showcasing%20multiple%20service%20platform%20features%20as%20interconnected%20floating%20cards%2C%20warm%20orange%20and%20teal%20color%20palette&width=600&height=400&seq=rel-features&orientation=landscape',
  'article-security': 'https://readdy.ai/api/search-image?query=A%20modern%20cybersecurity%20themed%20digital%20illustration%20showing%20a%20shield%20icon%2C%20warm%20dark%20background%20with%20orange%20accent%20lighting&width=600&height=400&seq=rel-security&orientation=landscape',
  'article-update': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20showing%20a%20platform%20update%20announcement%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-update&orientation=landscape',
  'article-faq-account': 'https://readdy.ai/api/search-image?query=A%20modern%20customer%20support%20illustration%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-faq-account&orientation=landscape',
  'article-comparison': 'https://readdy.ai/api/search-image?query=A%20modern%20comparison%20chart%20illustration%2C%20warm%20orange%20and%20teal%20color%20palette%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-comparison&orientation=landscape',
  'article-mobile': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20illustration%20of%20a%20smartphone%20displaying%20a%20clean%20mobile%20app%20interface%2C%20warm%20orange%20accents%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-mobile&orientation=landscape',
  'article-community': 'https://readdy.ai/api/search-image?query=A%20modern%20illustration%20representing%20online%20community%20guidelines%2C%20warm%20orange%20and%20cream%20color%20palette%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-community&orientation=landscape',
  'article-access-faq': 'https://readdy.ai/api/search-image?query=A%20modern%20technical%20troubleshooting%20illustration%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20flat%20design&width=600&height=400&seq=rel-access-faq&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <Layout>
        <SEO title="文章未找到 - 91.cc" description="抱歉，您查找的文章不存在或已被移除。" keywords="91.cc" canonicalUrl={`https://91.cc/article/${slug}`} />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
          <Link to="/" className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-1.5"></i>返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug);
  const category = categories.find((c) => c.slug === article.category);
  const canonicalUrl = `https://91.cc/article/${article.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    url: canonicalUrl,
    image: articleHeroImages[article.imageSeq] || '',
    publisher: {
      '@type': 'Organization',
      name: '91.cc 信息中心',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  return (
    <Layout>
      <SEO
        title={`${article.title} - 91.cc`}
        description={article.excerpt}
        keywords={`91.cc,${article.tags.join(',')},91.cc使用指南`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={articleHeroImages[article.imageSeq] || ''}
        jsonLd={jsonLd}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: category?.name || article.category, href: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="w-full bg-background-50 py-8 md:py-12" itemScope itemType="https://schema.org/Article">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight" itemProp="headline">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span itemProp="author" itemScope itemType="https://schema.org/Organization">
                <span itemProp="name">{article.author}</span>
              </span>
              <span className="text-foreground-300">·</span>
              <time dateTime={article.publishedAt} itemProp="datePublished">{article.publishedAt}</time>
              <span className="text-foreground-300">·</span>
              <span>阅读 {article.readTime} 分钟</span>
            </div>
          </header>

          <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
            <img
              src={articleHeroImages[article.imageSeq] || articleHeroImages['article-features']}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              itemProp="image"
            />
          </div>

          <div
            className="article-content max-w-none"
            itemProp="articleBody"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <footer className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>最后更新：</span>
              <time dateTime={article.updatedAt}>{article.updatedAt}</time>
            </div>
          </footer>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full bg-background-100 py-12 md:py-16" aria-labelledby="related-heading">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="related-heading" className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((item) => (
                <Link
                  key={item.slug}
                  to={`/article/${item.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={relatedThumbImages[item.imageSeq] || relatedThumbImages['article-features']}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2">阅读 {item.readTime} 分钟</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}